import dayjs from "dayjs";
import { message } from "./message";
import { sendReportData } from "@/api/report";
import { transformI18n } from "@/plugins/i18n";
import { responsiveStorageNameSpace } from "@/config";
import { useUserStoreHook } from "@/store/modules/user";
import { isAllEmpty, storageLocal } from "@pureadmin/utils";

/**
 * 获取当前选择的语言
 */
export function getCurrentLang(): string {
  return storageLocal().getItem<{ locale: "zh" | "en" }>(
    `${responsiveStorageNameSpace()}locale`
  ).locale;
}

type ResMessageType =
  | "save"
  | "add"
  | "edit"
  | "del"
  | "imp"
  | "cmd"
  | "upload"
  | "none";

/**
 * 全局消息提示
 * @param res HTTP响应结果
 * @param type 业务类型
 * @param onSuccess `code = 200`时的回调
 * @param onError 异常回调 `code != 200`, `code = 401` 的时候不执行
 */
export const ResMessage = (
  res: BaseResult,
  type: ResMessageType,
  onSuccess: (res: BaseResult) => void = () => {},
  onError?: (res: BaseResult) => void
) => {
  let success = "";
  switch (type) {
    case "none":
      success = "";
      break;
    case "save":
      success = "message.save";
      break;
    case "add":
      success = "message.add";
      break;
    case "edit":
      success = "message.edit";
      break;
    case "del":
      success = "message.delete";
      break;
    case "imp":
      success = "message.import";
      break;
    case "cmd":
      success = "message.command";
      break;
    case "upload":
      success = "message.upload";
      break;
    default:
      success = "类型异常";
  }

  if (res.code === 200) {
    if (type !== "none") {
      message(transformI18n(success), { type: "success" });
    }
    onSuccess(res);
  } else {
    if (res.code === 401) {
      // 登录过期
      useUserStoreHook().logOut();
      return;
    }
    onError
      ? onError(res)
      : message(res.text, {
          type: "error",
          duration: type === "del" ? 3000 : 2000
        });
  }
};

/**
 * 将字符串转为Base64，支持UTF-8编码（处理中文）
 * @param input - 输入字符串
 * @returns Base64编码字符串
 */
export function encodeToBase64(input: string): string {
  // 将字符串转为UTF-8编码的Uint8Array
  const utf8Array = new TextEncoder().encode(input);
  // 将Uint8Array转为Base64
  return btoa(String.fromCharCode(...utf8Array));
}

/**
 * 将Base64解码为字符串，支持UTF-8编码
 * @param base64 - Base64编码字符串
 * @returns 解码后的原始字符串
 */
export function decodeFromBase64(base64: string): string {
  // 将Base64解码为字符串
  const binaryString = atob(base64);
  // 将字符串转为Uint8Array
  const binaryArray = Uint8Array.from(binaryString, char => char.charCodeAt(0));
  // 使用TextDecoder解码为UTF-8字符串
  return new TextDecoder().decode(binaryArray);
}

/**
 * 格式化日期时间
 * @param val 日期
 * @returns `YYYY-MM-DD HH:mm:ss` 格式日期
 */
export function formatDateTime(
  val: dayjs.ConfigType,
  timezone = "Asia/Shanghai",
  format = "YYYY-MM-DD HH:mm:ss"
) {
  const d = dayjs(val);

  if (!d.isValid()) {
    return "--";
  }
  try {
    d.tz(timezone).format(format);
  } catch {
    return "--";
  }

  return d.tz(timezone).format(format);
}

/** 下载静态文件 */
export function downloadStaticFile(name: string, url: string) {
  const elink = document.createElement("a");
  elink.download = name;
  elink.style.display = "none";
  elink.href = url;
  document.body.appendChild(elink);
  elink.click();
  document.body.removeChild(elink);
}

/**
 * 处理空值
 * 如果传入值为空，则返回 `--`
 * 否则返回传入的值
 */
export function processEmptyValue<T = any>(val: T): T | "--" {
  return isAllEmpty(val) ? "--" : val;
}

/** 全局发送报告信息 */
export function handleSendReportData(data: object) {
  sendReportData(data)
    .then(() => {
      // console.log("发送成功", res);
    })
    .catch(() => {
      // console.log("发送失败");
    });
}

/** el-upload Ajax Error */
export class UploadAjaxError extends Error {
  name = "UploadAjaxError";
  status: number;
  method: string;
  url: string;

  constructor(message: string, status: number, method: string, url: string) {
    super(message);
    this.status = status;
    this.method = method;
    this.url = url;
  }
}
